import { useMemo } from 'react';
import { useQuery } from 'react-query';

import { orderRequisitesApi } from 'api';
import { Bank as BankEnum, OrderStatus, PaymentType, QueryKey } from 'enums';
import { Order, TradeMethod } from 'types';
import { authUtils, orderRequisitesUtils } from 'utils';

type UsePaymentTypeFlagsInput = {
  selectedTradeMethod: TradeMethod | undefined;
  order: Order | undefined;
};

export type UsePaymentTypeFlagsResult = {
  isNSPK: boolean;
  isCustomNspkForm: boolean;
  isTSBP: boolean;
  isDeeplink: boolean;
  isSBP: boolean;
  isSIM: boolean;
  isTSim: boolean;
  isSberPay: boolean;
  isCard2Card: boolean;
  isTCard2Card: boolean;
  isMobileOperator: boolean;
  isAmobile: boolean;
  isEcom: boolean;
  qrCodeUrl: string | undefined;
  nspkUrl: string | undefined;
  isNspkNamba: boolean;
  isVietnamSberQr: boolean;
  isExpayOneSberQr: boolean;
  isDeeplinkSberbank: boolean;
};

export const usePaymentTypeFlags = ({
  selectedTradeMethod,
  order,
}: UsePaymentTypeFlagsInput): UsePaymentTypeFlagsResult => {
  const paymentType = selectedTradeMethod?.paymentType ?? order?.payment?.type;

  const isNSPK = useMemo(() => paymentType === PaymentType.NSPK, [paymentType]);

  const isNspkNamba = useMemo(
    () =>
      isNSPK &&
      (order?.requisites?.bank === BankEnum.Namba ||
        order?.payment?.bank === BankEnum.Namba ||
        selectedTradeMethod?.bank === BankEnum.Namba),
    [
      isNSPK,
      order?.payment?.bank,
      order?.requisites?.bank,
      selectedTradeMethod?.bank,
    ],
  );

  const isVietnamSberQr = useMemo(
    () => isNSPK && !!order?.requisites?.paymentStringVietnamSberQR,
    [isNSPK, order?.requisites?.paymentStringVietnamSberQR],
  );

  // ExpayOne stays a provider, so the form is keyed off the payload the provider returned
  // rather than off the bank of the requisites
  const isExpayOneSberQr = useMemo(
    () => isNSPK && !!order?.requisites?.paymentStringExpayOneSberQR,
    [isNSPK, order?.requisites?.paymentStringExpayOneSberQR],
  );

  // NSPK scenarios that render the custom QR payment form instead of the default layout
  const isCustomNspkForm = useMemo(
    () => isNspkNamba || isVietnamSberQr || isExpayOneSberQr,
    [isNspkNamba, isVietnamSberQr, isExpayOneSberQr],
  );

  const isTSBP = useMemo(() => paymentType === PaymentType.TSBP, [paymentType]);

  const isDeeplink = useMemo(
    () => paymentType === PaymentType.Deeplink,
    [paymentType],
  );

  const isDeeplinkSberbank = useMemo(
    () => isDeeplink && order?.payment?.customerBank === BankEnum.Sberbank,
    [isDeeplink, order?.payment?.customerBank],
  );

  const isSBP = useMemo(() => paymentType === PaymentType.SBP, [paymentType]);

  const isSIM = useMemo(() => paymentType === PaymentType.SIM, [paymentType]);

  const isTSim = useMemo(() => paymentType === PaymentType.TSim, [paymentType]);

  const isSberPay = useMemo(
    () => paymentType === PaymentType.SberPay,
    [paymentType],
  );

  const isCard2Card = useMemo(
    () => paymentType === PaymentType.Card2Card,
    [paymentType],
  );

  const isTCard2Card = useMemo(
    () => paymentType === PaymentType.TCard2Card,
    [paymentType],
  );

  const isMobileOperator = useMemo(() => isSIM || isTSim, [isSIM, isTSim]);

  // Also key off the order's payment type: for ecom the card form must render while the order is still
  // New (before requisites), and selectedTradeMethod isn't set until the order leaves New.
  const isEcom = useMemo(
    () =>
      selectedTradeMethod?.paymentType === PaymentType.Ecom ||
      order?.payment?.type === PaymentType.Ecom,
    [selectedTradeMethod, order?.payment?.type],
  );

  const isAmobile = useMemo(
    () =>
      order?.requisites?.bank === BankEnum.Amobile ||
      order?.requisites?.bank === BankEnum.AmobileAlternate,
    [order?.requisites?.bank],
  );

  const { data: nspkDetails } = useQuery(
    QueryKey.NspkDetails,
    () => orderRequisitesApi.getNspkDetails(order?.id || ''),
    {
      retry: false,
      refetchOnWindowFocus: false,
      enabled: isNSPK && order?.status === OrderStatus.CustomerConfirm,
    },
  );

  const nspkUrl = useMemo(() => {
    if (isNSPK) {
      return nspkDetails?.qrLink;
    }
  }, [isNSPK, nspkDetails]);

  const qrCodeUrl = useMemo(() => {
    if (order?.requisites?.qrImageUrl) {
      return orderRequisitesUtils.getQrImageUrl(
        order.id,
        authUtils.getAuthData().token,
      );
    }
    if (!isNSPK && order?.requisites?.paymentLink) {
      return orderRequisitesUtils.getQrCodeUrl(order?.id || '');
    }
    return nspkDetails?.qrImage;
  }, [order, nspkDetails, isNSPK]);

  return {
    isNSPK,
    isCustomNspkForm,
    isTSBP,
    isDeeplink,
    isSBP,
    isSIM,
    isTSim,
    isSberPay,
    isCard2Card,
    isTCard2Card,
    isMobileOperator,
    isAmobile,
    isEcom,
    qrCodeUrl,
    nspkUrl,
    isVietnamSberQr,
    isExpayOneSberQr,
    isNspkNamba,
    isDeeplinkSberbank,
  };
};
